import React, { useState } from 'react';
import { Row, Col, Statistic, Drawer } from 'antd';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TrendingDown, Clock, Droplets } from 'lucide-react';

const KPIBanner = () => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const kpis = [
    { 
      value: 15, 
      suffix: '%', 
      title: t('kpi.avgCostReduction'),
      icon: <TrendingDown size={32} className="text-green-300" />,
      description: t('kpi.subtitle')
    },
    { 
      value: 50, 
      suffix: '%', 
      title: t('kpi.lessFieldTime'),
      icon: <Clock size={32} className="text-blue-300" />,
      description: t('kpi.subtitle')
    },
    { 
      value: 25, 
      suffix: '%', 
      title: t('kpi.waterSavings'),
      icon: <Droplets size={32} className="text-cyan-300" />,
      description: t('kpi.subtitle')
    }
  ];

  return (
    <div className="py-24 bg-gradient-to-r from-primary via-green-700 to-primary relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:50px_50px]"></div>
      </div>
      
      {/* Floating elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-green-300/10 rounded-full blur-2xl animate-pulse"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">{t('kpi.title')}</h2>
          <p className="text-xl text-green-100 max-w-2xl mx-auto">{t('kpi.subtitle')}</p>
        </motion.div>

        <Row gutter={[48, 48]} justify="center" align="middle">
          {kpis.map((kpi, index) => (
            <Col xs={24} md={8} key={index}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 30 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105">
                  <div className="mb-6 flex justify-center">
                    <div className="p-4 bg-white/10 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                      {kpi.icon}
                    </div>
                  </div>
                  
                  <Statistic
                    value={kpi.value}
                    suffix={kpi.suffix}
                    formatter={(value) => <CountUp end={value} duration={2.5} />}
                    valueStyle={{ 
                      color: 'white', 
                      fontSize: '4rem', 
                      fontWeight: '900',
                      lineHeight: '1'
                    }}
                  />
                  
                  <h3 className="text-xl font-bold text-white mt-4 mb-2">{kpi.title}</h3>
                  <p className="text-green-100 text-sm">{kpi.description}</p>
                </div>
              </motion.div>
            </Col>
          ))}
        </Row>
        
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          viewport={{ once: true }}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-block px-8 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 hover:bg-white/15 transition text-sm text-green-100 font-medium"
          >
            📊 {t('kpi.source')}
          </button>
        </motion.div>

        <Drawer
          title={t('kpi.sourceTitle')}
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={window.innerWidth <= 768 ? '100%' : 720}
        >
          <div className="h-[70vh]">
            <iframe
              title="FieldOps Case Study"
              src="https://blog.agcurate.com/from-day-zero-to-delivery-what-a-fully-visible-farming-season-looks-like-with-fieldops/?utm=fieldops-landing-page-kpi-source"
              className="w-full h-full border-0 rounded"
            />
          </div>
        </Drawer>
      </div>
    </div>
  );
};

export default KPIBanner;
